import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { SllModule, SllLesson } from '@types';
import SectionContent from '../section-content/SectionContent';
import { useEditLesson } from '../../useEditLesson';
import { LessonType, useSllMoveModuleMutation, SllModuleType } from '@api/graphql-codegen';
import type { DropResult, ResponderProvided, DraggableProvided, DroppableProvided, DraggableStateSnapshot } from '@src/utils/componentCasts';
import { DragDropContext, Droppable, Draggable } from '@src/utils/componentCasts';

type Props = {
  contents: SllModule[];
  lesson?: SllLesson;
  isHEPreparation?: boolean;
  lessonType: LessonType | undefined;
};

const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

function SectionContents(props: Props) {
  const { contents } = props;
  const [ localContents, setLocalContents ] = useState<Array<SllModule>>([]);
  const { content: selectedContent, lesson, setContent, setLesson, setSelectionLevel } = useEditLesson();

  const [sllMoveModule] = useSllMoveModuleMutation()
  useEffect(() => {
    setLocalContents(contents);
  }, [contents])

  const handleDragEnd = useCallback(async (result: DropResult, provided?: ResponderProvided) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    const reorderedSubItems = reorder(
      localContents,
      sourceIndex,
      destIndex
    );

    const selectedModule = (reorderedSubItems as SllModule[]).find((content: SllModule, index) => index === destIndex);
    setLocalContents(reorderedSubItems as SllModule[]);
    if (lesson && selectedModule) {
      const moved = await sllMoveModule({
        variables: {
          input: {
            lessonId: lesson?.id,
            id: selectedModule.id,
            type: SllModuleType.MATERIAL,
            oldPosition: {
              sectionId: undefined,
              position: result.source!.index + 1
            },
            newPosition: {
              sectionId: undefined,
              position: result.destination!.index + 1
            }
          }
        }
      });
      if (moved.data?.sllMoveModule) {
        setSelectionLevel('Lesson');
        setLesson(lesson);
        setContent(undefined);
      }
    }
  }, [lesson, localContents, sllMoveModule, setContent, setLesson, setSelectionLevel]);

  const modules = useMemo(() => [...localContents || []], [localContents])

  return (
    // @ts-ignore
    <DragDropContext onDragEnd={handleDragEnd}>
      // @ts-ignore
      <Droppable droppableId="droppableContent" type="teacherModuleItem">
        {(droppableProvided: DroppableProvided) => (
          <div
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
          >
            {modules.map((item: SllModule, index: number) => (
              // @ts-ignore
              <Draggable
                key={item.id}
                draggableId={item.id}
                index={index}
              >
                {(
                  draggableProvided: DraggableProvided,
                  snapshot: DraggableStateSnapshot
                ) => {
                  const sectionSelected = selectedContent?.id === item.id;
                  return (
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                    >
                      <SectionContent draggableProvided={draggableProvided} snapshot={snapshot} modules={modules} key={item.id} selected={sectionSelected} content={item} isPreparation />
                    </div>
                  );
                }}
              </Draggable>
            ))}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
  </DragDropContext>
  );
}

export default SectionContents;